/**
 * @fileoverview Branch Analytics — Branch-wise Income Share page.
 * @description Ported from the InsightBox Banking design system's own
 * `BranchAnalytics/IncomeShare.tsx`. Always a cross-branch comparison (that's the page's whole
 * purpose), so no Branch filter — but a Bank Regional Manager still only ranks their own region's
 * branches against each other, via `bankingScope.regionBranchIds` (mirrors the reference's own
 * `scopeBranchIds` behavior for this exact page). A Bank Branch Manager has no region lock, so they
 * see the full bank-wide ranking here, same as every unrestricted role.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  ComboBarDot,
  FIVE_YEAR_ID,
  FyFilterSelect,
  LastUpdatedLabel,
  branchIncome,
  branchIncome5YSum,
  formatLacs,
  prevFY,
  resolveBankingAccessScope,
  resolveFy,
  trendLabel,
  useBankingData,
  zigzagOrder,
} from '@/features/banking'
import SegmentedControl from '@/shared/components/atoms/SegmentedControl'
import BankingKpiStrip from '@/shared/components/molecules/BankingKpiStrip'
import BusinessSummaryPanel from '@/shared/components/molecules/BusinessSummaryPanel'
import ChartCard from '@/shared/components/molecules/ChartCard'
import PageHeader from '@/shared/components/molecules/PageHeader'

import type { ComboBarDotPoint, FyFilter } from '@/features/banking'
import type { BankingKpiStripItem } from '@/shared/components/molecules/BankingKpiStrip'

const RANK_VIEWS = [
  { value: 'all', label: 'All' },
  { value: 'top', label: 'Top 10' },
  { value: 'bottom', label: 'Bottom 10' },
] as const
type RankView = (typeof RANK_VIEWS)[number]['value']

/**
 * @component
 */
function BankingBranchAnalyticsIncomeSharePage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [fyFilter, setFyFilter] = useState<FyFilter>(FIVE_YEAR_ID)
  const [rankView, setRankView] = useState<RankView>('all')
  const is5Years = fyFilter === FIVE_YEAR_ID
  const fy = resolveFy(fyFilter)

  const rankViewOptions = RANK_VIEWS.map((v) => ({
    value: v.value,
    label: t(`banking.branchAnalyticsIncomeShare.rankView.${v.value}`),
  }))

  const header = (
    <PageHeader
      kicker={t('banking.branchAnalyticsIncomeShare.kicker')}
      title={t('banking.branchAnalyticsIncomeShare.title')}
      description={
        is5Years
          ? t('banking.branchAnalyticsIncomeShare.descriptionFiveYear')
          : t('banking.branchAnalyticsIncomeShare.descriptionYear', { fy })
      }
      filters={
        <>
          <FyFilterSelect value={fyFilter} onChange={setFyFilter} />
          {dataQuery.data && <LastUpdatedLabel value={dataQuery.data.meta.lastUpdated} />}
        </>
      }
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <BankingKpiStrip
          loading
          items={[
            { label: '', value: '', highlight: true },
            { label: '', value: '' },
            { label: '', value: '' },
            { label: '', value: '' },
          ]}
        />
        <ChartCard title="" loading>
          <div />
        </ChartCard>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data

  // `rows` is the whole network — every KPI/summary stat describes it in full, regardless of how
  // the chart itself is scoped below. That's what keeps "Top 10"/"Bottom 10" a pure display filter
  // rather than something that quietly changes what the numbers above the chart mean. "5 Years"
  // ranks/sums each branch's income across FY21-FY25 (income is additive, so summing is
  // meaningful); an individual year ranks that year's own figures — the same "5Y Sum" idea used on
  // Bank Overview, Total Business, Net Profit, etc.
  const rows = is5Years
    ? branchIncome5YSum(data, bankingScope.regionBranchIds)
    : branchIncome(data, fy, bankingScope.regionBranchIds)
  const total = rows.reduce((sum, r) => sum + r.incomeLacs, 0)
  const top3Share = rows.slice(0, 3).reduce((sum, r) => sum + r.sharePct, 0)
  const avgShare = 100 / rows.length

  // Prior year's share per branch — only meaningful in single-year mode, where "YoY" describes an
  // actual year-over-year change; suppressed in "5 Years" mode since the top branch there is
  // ranked by a 5-year sum, not a single year, so a YoY delta wouldn't describe the same thing.
  const priorFy = prevFY(fy)
  const prevShareByBranch = new Map(
    !is5Years && priorFy
      ? branchIncome(data, priorFy, bankingScope.regionBranchIds).map((r) => [
          r.branch.id,
          r.sharePct,
        ])
      : [],
  )
  const topPrevShare = prevShareByBranch.get(rows[0]!.branch.id)
  const topShareDelta = topPrevShare !== undefined ? rows[0]!.sharePct - topPrevShare : null

  // `rows` is already sorted largest-to-smallest, so the chart's own scope is just a slice of it —
  // the network can grow well past what fits legibly in one view (50+ branches) without the chart
  // becoming unreadable.
  const chartRows =
    rankView === 'top' ? rows.slice(0, 10) : rankView === 'bottom' ? rows.slice(-10) : rows

  // The zigzag reorder is purely for the line's up/down visual rhythm — ranking/KPIs above always
  // use the true sorted `rows`.
  const chartData: ComboBarDotPoint[] = zigzagOrder(chartRows).map((r) => ({
    label: r.branch.shortName,
    count: r.incomeLacs,
    sharePct: r.sharePct,
    ...(prevShareByBranch.has(r.branch.id) && {
      prevSharePct: prevShareByBranch.get(r.branch.id)!,
    }),
  }))

  const kpis: BankingKpiStripItem[] = [
    {
      label: is5Years
        ? t('banking.branchAnalyticsIncomeShare.kpi.totalIncome5Y')
        : t('banking.branchAnalyticsIncomeShare.kpi.totalIncomeYear', { fy }),
      value: formatLacs(total),
      sublabel: is5Years ? t('banking.branchAnalyticsIncomeShare.kpi.fiveYearCombined') : '',
      highlight: true,
    },
    {
      label: t('banking.branchAnalyticsIncomeShare.kpi.topBranch'),
      value: rows[0]!.branch.shortName,
      sublabel:
        topShareDelta !== null
          ? t('banking.branchAnalyticsIncomeShare.kpi.topBranchSublabelDelta', {
              pct: rows[0]!.sharePct.toFixed(2),
              sign: topShareDelta >= 0 ? '+' : '',
              delta: topShareDelta.toFixed(2),
            })
          : t('banking.branchAnalyticsIncomeShare.kpi.topBranchSublabel', {
              pct: rows[0]!.sharePct.toFixed(2),
              value: formatLacs(rows[0]!.incomeLacs),
            }),
      ...(topShareDelta !== null && { trend: { direction: trendLabel(topShareDelta) } }),
    },
    {
      label: t('banking.branchAnalyticsIncomeShare.kpi.top3Share'),
      value: `${top3Share.toFixed(2)}%`,
      sublabel: formatLacs(rows.slice(0, 3).reduce((sum, r) => sum + r.incomeLacs, 0)),
    },
    {
      label: t('banking.branchAnalyticsIncomeShare.kpi.avgBranchShare'),
      value: `${avgShare.toFixed(2)}%`,
      sublabel: formatLacs(total / rows.length),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      {header}
      <BankingKpiStrip items={kpis} />

      <ChartCard
        fill
        title={
          is5Years
            ? t('banking.branchAnalyticsIncomeShare.chartTitleFiveYear')
            : t('banking.branchAnalyticsIncomeShare.chartTitleYear', { fy })
        }
        subtitle={
          rankView !== 'all'
            ? t('banking.branchAnalyticsIncomeShare.chartSubtitleRanked', {
                view: t(`banking.branchAnalyticsIncomeShare.rankView.${rankView}`),
                count: rows.length,
              })
            : ''
        }
        action={
          rows.length > 10 ? (
            <SegmentedControl value={rankView} onChange={setRankView} options={rankViewOptions} />
          ) : undefined
        }
      >
        <div className="relative min-h-0 flex-1">
          <ComboBarDot
            data={chartData}
            barName={
              is5Years
                ? t('banking.branchAnalyticsIncomeShare.barName5Y')
                : t('banking.branchAnalyticsIncomeShare.barName')
            }
            countFormatter={(v) => formatLacs(v)}
            height="100%"
          />
        </div>
      </ChartCard>

      <BusinessSummaryPanel
        title={t('banking.businessSummary')}
        lines={[
          is5Years
            ? t('banking.branchAnalyticsIncomeShare.summary.fiveYearLine1', {
                branch: rows[0]!.branch.shortName,
                pct: rows[0]!.sharePct.toFixed(2),
                value: formatLacs(rows[0]!.incomeLacs),
              })
            : t('banking.branchAnalyticsIncomeShare.summary.yearLine1', {
                branch: rows[0]!.branch.shortName,
                pct: rows[0]!.sharePct.toFixed(2),
                value: formatLacs(rows[0]!.incomeLacs),
                fy,
              }),
          is5Years
            ? t('banking.branchAnalyticsIncomeShare.summary.fiveYearLine2', {
                branch1: rows[0]!.branch.shortName,
                branch2: rows[1]!.branch.shortName,
                branch3: rows[2]!.branch.shortName,
                pct: top3Share.toFixed(2),
                value: formatLacs(rows.slice(0, 3).reduce((sum, r) => sum + r.incomeLacs, 0)),
              })
            : t('banking.branchAnalyticsIncomeShare.summary.yearLine2', {
                branch1: rows[0]!.branch.shortName,
                branch2: rows[1]!.branch.shortName,
                branch3: rows[2]!.branch.shortName,
                pct: top3Share.toFixed(2),
                value: formatLacs(rows.slice(0, 3).reduce((sum, r) => sum + r.incomeLacs, 0)),
              }),
        ]}
      />
    </div>
  )
}

export default BankingBranchAnalyticsIncomeSharePage
